/* @flow */
import idx from 'idx';
import * as React from 'react';
import Modal from 'react-modal';
import Row from 'components/Row';
import Time from 'components/Time';
import Text from 'components/Text';
import Image from 'components/Image';
import Button from 'components/Button';
import Heading from 'components/Heading';
import { withRouter } from 'app/context/Router';
import WizardFooter from 'components/WizardFooter';
import WizardHeader from 'components/WizardHeader';
import CanvasLTICredentialsModal from 'admin/components/CanvasLTICredentialsModal';
import IntegrationWizardHeaderContent from 'admin/components/IntegrationWizardHeaderContent';

import './styles/LTISetup.scss';

function Step(props: { title: string, children: React.Node }) {
  return (
    <Row size="large">
      <Row size="small">
        <Text size="xlarge" element="div">
          {props.title}
        </Text>
      </Row>
      {props.children}
    </Row>
  );
}

type State = {
  isCredentialModalOpened: boolean,
};

type Props = {
  lti: any,
  router: any,
  name: string,
  logo: string,
  images: string[],
  onDone: Function,
  stepsFn: Function,
  deleteLTI: Function,
  rotateCredentials: Function,
};

class LTISetup extends React.Component<Props, State> {
  state: State = {
    isCredentialModalOpened: false,
  };

  renderKeyGenerator() {
    const clientSecretUpdatedAt = idx(this.props, _ => _.lti.config.clientSecretUpdatedAt);
    const configURL = idx(this.props, _ => _.lti.config.configUrl);
    const hasLTICredentials = !!configURL;

    if (!hasLTICredentials) {
      return (
        <div className="LTISetup-content-credentials">
          <Button onClick={() => this.setState({ isCredentialModalOpened: true })}>
            Generate Credentials
          </Button>
        </div>
      );
    }

    return (
      <div className="LTISetup-content-credentials LTISetup-content-credentials-box">
        <Row size="small" element="div">
          For greater security, username and password credentials are provided once. They’ll be
          hidden when you return to this page. Click below to revoke or regenerate your security
          credentials.
        </Row>
        <div>
          <div className="LTISetup-content-credentials-actions">
            <Button
              color="brand"
              level="secondary"
              onClick={() => this.setState({ isCredentialModalOpened: true })}>
              Regenerate credentials
            </Button>
            <Button color="lava" level="secondary" onClick={() => this.props.deleteLTI()}>
              Revoke credentials
            </Button>
          </div>
          {clientSecretUpdatedAt && (
            <Text color="eclipse">
              Last generated{' '}
              <Time
                date={new Date(clientSecretUpdatedAt)}
                format="long"
                year="numeric"
                month="long"
                weekday="long"
                day="numeric"
              />
            </Text>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { name, lti } = this.props;

    const configURL = idx(this.props, _ => _.lti.config.configUrl);
    const hasLTICredentials = !!configURL;
    const steps = this.props.stepsFn(lti);

    const title = `1. Log in to your ${name} Account in a new tab or window to complete the following:`;

    return (
      <div className="LTISetup">
        <Modal isOpen={this.state.isCredentialModalOpened}>
          <CanvasLTICredentialsModal
            rotateCredentials={this.props.rotateCredentials}
            onRequestHide={() => this.setState({ isCredentialModalOpened: false })}
          />
        </Modal>

        <WizardHeader>
          <IntegrationWizardHeaderContent
            title={`${this.props.name} LTI Configuration`}
            iconURL={this.props.logo}
          />
        </WizardHeader>
        <div className="LTISetup-content">
          <Heading level="1">Configuring {this.props.name} LTI for an organization</Heading>
          <div className="LTISetup-content-step1">
            <Step title={title}>
              <Row>
                <ol className="LTISetup-content-substeps">
                  {steps.steps.map((step, index) => (
                    <li key={index}>
                      {index + 1}. {step}
                      {index + 1 === steps.keyGeneratorStep && this.renderKeyGenerator()}
                    </li>
                  ))}
                </ol>
              </Row>
              <div className="LTISetup-content-images">
                {this.props.images.map((image, index) => (
                  <Image key={index} src={image} />
                ))}
              </div>
            </Step>
            <div className="LTISetup-content-step1-note">
              <Text className="LTISetup-content-step1-note-text" element="div">
                <Text weight="bold">Note:</Text> Make sure to keep this window open while you access{' '}
                {this.props.name} in another tab so that you can refer back to the directions.
              </Text>
            </div>
          </div>
        </div>
        <WizardFooter>
          <div className="LTISetup-footer">
            <Button onClick={this.props.router.goBack} level="secondary">
              Back
            </Button>
            <Button disabled={!hasLTICredentials} onClick={this.props.onDone}>
              Complete
            </Button>
          </div>
        </WizardFooter>
      </div>
    );
  }
}

export default withRouter(LTISetup);
